import {
  QuestionEvaluation,
  QuestionType,
  ReliabilityAssessment,
  ReliabilityCheck,
  ReviewFlag,
  RubricAlignment,
  ScoreAlignmentCheck,
  ScoreDecision,
  StudentFeedback,
  TeacherViewSummary,
  TranscriptionResult,
} from '../types';

export const REVIEW_CONFIDENCE_THRESHOLD = 0.75;

const DETERMINISTIC_TYPES: QuestionType[] = [
  'objective_exact',
  'objective_structured',
  'diagram_labeling',
];

export const isDeterministicQuestionType = (questionType: QuestionType): boolean =>
  DETERMINISTIC_TYPES.includes(questionType);

export const clampConfidence = (value: number | undefined): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(1, value));
};

export const computeOverallConfidence = (
  transcriptionConfidence: number,
  formatMatchConfidence: number,
  scoringConfidence: number,
  feedbackConfidence = 1,
): number => {
  return clampConfidence(
    Math.min(
      transcriptionConfidence,
      formatMatchConfidence,
      scoringConfidence,
      feedbackConfidence,
    ),
  );
};

export const normalizeRubricLevel = (
  rubricLevel: string | undefined,
  awardedMarks: number,
  maxMarks: number,
): string => {
  if (rubricLevel?.trim()) {
    return rubricLevel.trim();
  }

  if (maxMarks <= 0) return 'Needs Improvement';
  const ratio = awardedMarks / maxMarks;

  if (ratio >= 0.9) return 'Exemplary';
  if (ratio >= 0.7) return 'Competent';
  if (ratio >= 0.4) return 'Developing';
  return 'Needs Improvement';
};

export const mergeReviewFlags = (flags: ReviewFlag[]): ReviewFlag[] => {
  const seen = new Set<string>();

  return flags.filter((flag) => {
    const key = `${flag.type}:${flag.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildCheck = (
  code: ReliabilityCheck['code'],
  passed: boolean,
  reason: string,
): ReliabilityCheck => ({
  code,
  passed,
  reason,
});

const SCORE_EPSILON = 0.001;

const nearlyEqual = (left: number, right: number): boolean =>
  Math.abs(left - right) <= SCORE_EPSILON;

const normalizeLevelLabel = (value: string | undefined): string =>
  (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

const isWholeNumber = (value: number): boolean => nearlyEqual(value, Math.round(value));

const scoreExplanationText = (scoring: ScoreDecision): string =>
  [
    scoring.scoreReason,
    scoring.rubricLevelRuleApplied,
    scoring.partialCreditRuleApplied,
    ...scoring.criterionScores.map((criterionScore) => criterionScore.rationale),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const explicitFractionalMarksAllowed = (
  rubricAlignment: RubricAlignment,
  scoring: ScoreDecision,
): boolean => {
  const allowedMarks = rubricAlignment.levelMarkRules.flatMap((rule) => rule.allowedMarks);
  if (allowedMarks.some((mark) => !isWholeNumber(Number(mark)))) return true;
  return /\b(half|0\.5|\.5|decimal|fractional|fraction)\b/i.test(scoreExplanationText(scoring));
};

const getAllowedMarks = (rubricAlignment: RubricAlignment, maxMarks: number): number[] => {
  const allowed = rubricAlignment.levelMarkRules
    .flatMap((rule) => rule.allowedMarks)
    .map(Number)
    .filter((mark) => Number.isFinite(mark) && mark >= 0 && mark <= maxMarks);

  return Array.from(new Set(allowed)).sort((left, right) => left - right);
};

const scoreUsesAllowedMark = (awardedMarks: number, allowedMarks: number[]): boolean =>
  allowedMarks.some((allowedMark) => nearlyEqual(allowedMark, awardedMarks));

export const buildScoreAlignmentCheck = (
  rubricAlignment: RubricAlignment,
  scoring: ScoreDecision,
): ScoreAlignmentCheck => {
  const issues: string[] = [];
  const criterionById = new Map(
    rubricAlignment.criteria.map((criterion) => [criterion.criterionId, criterion]),
  );
  const scoredCriterionIds = new Set<string>();

  const totalFromCriteria = scoring.criterionScores.reduce(
    (sum, criterionScore) => sum + Number(criterionScore.awardedMarks || 0),
    0,
  );
  const maxFromCriteria = scoring.criterionScores.reduce(
    (sum, criterionScore) => sum + Number(criterionScore.maxMarks || 0),
    0,
  );

  if (Math.abs(totalFromCriteria - scoring.awardedMarks) > SCORE_EPSILON) {
    issues.push('Total awarded marks do not match the sum of criterion-level marks.');
  }

  if (scoring.criterionScores.length > 0 && Math.abs(maxFromCriteria - scoring.maxMarks) > SCORE_EPSILON) {
    issues.push('Total max marks do not match the sum of criterion-level max marks.');
  }

  rubricAlignment.criteria.forEach((criterion) => {
    if (
      !scoring.criterionScores.some(
        (criterionScore) => criterionScore.criterionId === criterion.criterionId,
      )
    ) {
      issues.push(`Criterion ${criterion.criterionId} is missing a criterion-level score.`);
    }
  });

  scoring.criterionScores.forEach((criterionScore) => {
    if (scoredCriterionIds.has(criterionScore.criterionId)) {
      issues.push(`Criterion ${criterionScore.criterionId} was scored more than once.`);
    }
    scoredCriterionIds.add(criterionScore.criterionId);

    const criterion = criterionById.get(criterionScore.criterionId);
    if (!criterion) {
      issues.push(`Criterion ${criterionScore.criterionId} was scored without a rubric mapping.`);
      return;
    }

    if (criterion.judgment !== 'unmet' && criterion.supportingEvidenceIds.length === 0) {
      issues.push(`Criterion ${criterion.criterionId} was scored from a ${criterion.judgment} judgment without evidence.`);
    }

    if (criterion.judgment === 'unmet' && criterionScore.awardedMarks !== 0) {
      issues.push(`Criterion ${criterion.criterionId} was marked unmet but received non-zero marks.`);
    }

    if (
      criterion.judgment === 'partial' &&
      (criterionScore.awardedMarks <= 0 || criterionScore.awardedMarks >= criterionScore.maxMarks)
    ) {
      issues.push(`Criterion ${criterion.criterionId} was marked partial but the marks were not partial.`);
    }

    if (criterion.judgment === 'met' && criterionScore.awardedMarks <= 0) {
      issues.push(`Criterion ${criterion.criterionId} was marked met but received zero marks.`);
    }
  });

  if (rubricAlignment.levelMarkRules.length === 0) {
    issues.push('Rubric level mark rules were not extracted, so the score cannot be validated.');
  } else {
    const allowedMarks = getAllowedMarks(rubricAlignment, scoring.maxMarks);
    if (allowedMarks.length > 0 && !scoreUsesAllowedMark(scoring.awardedMarks, allowedMarks)) {
      issues.push(
        `Awarded marks ${scoring.awardedMarks} is not an allowed mark value for this rubric. Allowed values: ${allowedMarks.join(', ')}.`,
      );
    }

    const matchingRule = rubricAlignment.levelMarkRules.find(
      (rule) => normalizeLevelLabel(rule.level) === normalizeLevelLabel(scoring.rubricLevel),
    );
    if (!matchingRule) {
      issues.push(`Rubric level "${scoring.rubricLevel}" does not match any extracted rubric level rule.`);
    } else {
      const markInRange =
        scoring.awardedMarks >= matchingRule.minMarks - SCORE_EPSILON &&
        scoring.awardedMarks <= matchingRule.maxMarks + SCORE_EPSILON;
      const markAllowed =
        matchingRule.allowedMarks.length === 0 ||
        matchingRule.allowedMarks.some((mark) => nearlyEqual(Number(mark), scoring.awardedMarks));

      if (!markInRange || !markAllowed) {
        issues.push(
          `Rubric level "${scoring.rubricLevel}" does not permit awarded marks ${scoring.awardedMarks}.`,
        );
      }
    }
  }

  if (!isWholeNumber(scoring.awardedMarks) && !explicitFractionalMarksAllowed(rubricAlignment, scoring)) {
    issues.push('Fractional marks were awarded without an explicit rubric rule allowing fractional credit.');
  }

  return {
    passed: issues.length === 0,
    issues,
  };
};

const containsInflatedPraise = (text: string): boolean =>
  /\b(excellent|perfect|outstanding|great|strong)\b/i.test(text);

const containsQuantitativeOverclaim = (text: string): boolean => {
  const hasAbsoluteClaim = /\b(100%|all|none|no food|gone|lost|wipe(?:d)? out|die out|extinct)\b/i.test(text);
  const hasDataContext = /\b(%|percent|data|crop|food|supply|depend|pollinator)\b/i.test(text);
  return hasAbsoluteClaim && hasDataContext;
};

const buildScoringSupportIssues = (
  transcription: TranscriptionResult,
  rubricAlignment: RubricAlignment,
  scoring: ScoreDecision,
): string[] => {
  const issues: string[] = [];
  const validEvidenceIds = new Set(transcription.evidenceSnippets.map((snippet) => snippet.id));
  const criterionById = new Map(
    rubricAlignment.criteria.map((criterion) => [criterion.criterionId, criterion]),
  );
  const criterionEvidenceIds = new Set(
    rubricAlignment.criteria.flatMap((criterion) => criterion.supportingEvidenceIds),
  );

  scoring.scoringEvidenceIds.forEach((evidenceId) => {
    if (!validEvidenceIds.has(evidenceId)) {
      issues.push(`Score referenced unknown evidence id ${evidenceId}.`);
    }
  });

  if (scoring.awardedMarks > 0 && scoring.scoringEvidenceIds.length === 0 && criterionEvidenceIds.size === 0) {
    issues.push('Positive marks were awarded without scoring evidence IDs or criterion evidence.');
  }

  scoring.criterionScores.forEach((criterionScore) => {
    const criterion = criterionById.get(criterionScore.criterionId);
    if (!criterion) return;
    if (criterionScore.awardedMarks > 0 && criterion.supportingEvidenceIds.length === 0) {
      issues.push(`Criterion ${criterion.criterionId} received marks without supporting evidence.`);
    }
  });

  return issues;
};

const buildFeedbackSupportIssues = (
  transcription: TranscriptionResult,
  rubricAlignment: RubricAlignment,
  scoring: ScoreDecision,
  feedback: StudentFeedback,
): string[] => {
  const issues: string[] = [];
  const validEvidenceIds = new Set(transcription.evidenceSnippets.map((snippet) => snippet.id));
  const validCriterionIds = new Set(
    rubricAlignment.criteria.map((criterion) => criterion.criterionId),
  );

  feedback.evidenceIds.forEach((evidenceId) => {
    if (!validEvidenceIds.has(evidenceId)) {
      issues.push(`Feedback referenced unknown evidence id ${evidenceId}.`);
    }
  });

  feedback.criterionIds.forEach((criterionId) => {
    if (!validCriterionIds.has(criterionId)) {
      issues.push(`Feedback referenced unknown criterion id ${criterionId}.`);
    }
  });

  feedback.unsupportedStatements.forEach((statement) => {
    if (statement.trim()) {
      issues.push(`Feedback included an unsupported statement: ${statement.trim()}`);
    }
  });

  const validReferencedEvidenceCount = feedback.evidenceIds.filter((evidenceId) =>
    validEvidenceIds.has(evidenceId),
  ).length;
  if (validReferencedEvidenceCount === 0 && transcription.answerDetected) {
    issues.push('Feedback did not reference any valid evidence snippet.');
  }

  if (scoring.awardedMarks === 0 && containsInflatedPraise(feedback.whatYouDidWell)) {
    issues.push('Feedback used inflated praise despite a zero score.');
  }

  const feedbackText = [
    feedback.whatYouDidWell,
    feedback.whatToImprove,
    feedback.tryThisNextTime,
  ].join(' ');
  if (containsQuantitativeOverclaim(feedbackText) && !containsQuantitativeOverclaim(transcription.rawAnswer)) {
    issues.push('Feedback may turn partial or dependency data into an unsupported absolute claim.');
  }

  return issues;
};

export const buildReliabilityStage = (
  transcription: TranscriptionResult,
  rubricAlignment: RubricAlignment,
  scoring: ScoreDecision,
  scoreAlignment: ScoreAlignmentCheck,
  feedback: StudentFeedback,
): {
  reliability: ReliabilityAssessment;
  reviewFlags: ReviewFlag[];
  overallConfidence: number;
} => {
  const checks: ReliabilityCheck[] = [];
  const reviewFlags: ReviewFlag[] = [];
  const scoringIssues = buildScoringSupportIssues(transcription, rubricAlignment, scoring);
  const feedbackIssues = buildFeedbackSupportIssues(transcription, rubricAlignment, scoring, feedback);

  if (!transcription.answerDetected) {
    reviewFlags.push({
      type: 'missing_answer',
      severity: 'medium',
      message: 'No answer was detected for this question.',
    });
  }

  if (transcription.transcriptionConfidence < REVIEW_CONFIDENCE_THRESHOLD) {
    reviewFlags.push({
      type: 'low_transcription_confidence',
      severity: 'high',
      message: 'The answer could not be read with enough confidence.',
    });
  }

  if (rubricAlignment.formatMatchConfidence < REVIEW_CONFIDENCE_THRESHOLD) {
    reviewFlags.push({
      type: 'format_mismatch',
      severity: 'medium',
      message: 'The answer does not clearly match the expected response format.',
    });
  }

  if (scoring.scoringConfidence < REVIEW_CONFIDENCE_THRESHOLD) {
    reviewFlags.push({
      type: 'ai_uncertainty',
      severity: 'high',
      message: 'The scoring rationale is not confident enough for automatic approval.',
    });
  }

  if (rubricAlignment.questionType.includes('diagram') && scoring.scoringConfidence < 0.8) {
    reviewFlags.push({
      type: 'diagram_unclear',
      severity: 'medium',
      message: 'The diagram-based judgement should be checked by the teacher.',
    });
  }

  const unsupportedClaimIssue =
    rubricAlignment.unsupportedClaims.length > 0
      ? `Rubric mapping produced unsupported claims: ${rubricAlignment.unsupportedClaims.join('; ')}`
      : 'All rubric claims are backed by evidence.';
  checks.push(
    buildCheck(
      'unsupported_claim',
      rubricAlignment.unsupportedClaims.length === 0,
      unsupportedClaimIssue,
    ),
  );

  if (rubricAlignment.unsupportedClaims.length > 0) {
    reviewFlags.push({
      type: 'unsupported_claim',
      severity: 'high',
      message: unsupportedClaimIssue,
    });
  }

  checks.push(
    buildCheck(
      'score_rubric_drift',
      scoreAlignment.passed,
      scoreAlignment.passed
        ? 'Score aligns with rubric mapping.'
        : scoreAlignment.issues.join(' '),
    ),
  );

  if (!scoreAlignment.passed) {
    reviewFlags.push({
      type: 'score_rubric_drift',
      severity: 'high',
      message: scoreAlignment.issues.join(' '),
    });

    if (
      scoreAlignment.issues.some((issue) =>
        /allowed mark|fractional/i.test(issue),
      )
    ) {
      reviewFlags.push({
        type: 'invalid_score_increment',
        severity: 'high',
        message: 'The score uses a mark value that is not permitted by the rubric.',
      });
    }

    if (
      scoreAlignment.issues.some((issue) =>
        /rubric level/i.test(issue),
      )
    ) {
      reviewFlags.push({
        type: 'rubric_level_mismatch',
        severity: 'high',
        message: 'The rubric level does not match the awarded marks or extracted level rules.',
      });
    }

    if (
      scoreAlignment.issues.some((issue) =>
        /missing a criterion-level score|scored more than once|without a rubric mapping/i.test(issue),
      )
    ) {
      reviewFlags.push({
        type: 'missing_criterion_score',
        severity: 'high',
        message: 'Criterion-level scoring is incomplete or uses invalid criterion IDs.',
      });
    }
  }

  checks.push(
    buildCheck(
      'unsupported_scoring_claim',
      scoringIssues.length === 0,
      scoringIssues.length === 0
        ? 'Score references valid evidence and scored criteria.'
        : scoringIssues.join(' '),
    ),
  );

  if (scoringIssues.length > 0) {
    reviewFlags.push({
      type: 'unsupported_scoring_claim',
      severity: 'high',
      message: scoringIssues.join(' '),
    });
  }

  checks.push(
    buildCheck(
      'feedback_not_supported',
      feedbackIssues.length === 0,
      feedbackIssues.length === 0
        ? 'Feedback references valid evidence and rubric criteria.'
        : feedbackIssues.join(' '),
    ),
  );

  if (feedbackIssues.length > 0) {
    reviewFlags.push({
      type: 'feedback_not_supported',
      severity: 'high',
      message: feedbackIssues.join(' '),
    });

    if (feedbackIssues.some((issue) => /absolute claim|dependency data/i.test(issue))) {
      reviewFlags.push({
        type: 'quantitative_overclaim',
        severity: 'high',
        message: 'Feedback may overstate a quantitative or dependency-based conclusion.',
      });
    }
  }

  let overallConfidence = computeOverallConfidence(
    transcription.transcriptionConfidence,
    rubricAlignment.formatMatchConfidence,
    scoring.scoringConfidence,
    feedback.feedbackConfidence,
  );

  if (rubricAlignment.unsupportedClaims.length > 0) {
    overallConfidence = Math.min(overallConfidence, 0.5);
  }
  if (!scoreAlignment.passed) {
    overallConfidence = Math.min(overallConfidence, 0.45);
  }
  if (scoringIssues.length > 0) {
    overallConfidence = Math.min(overallConfidence, 0.45);
  }
  if (feedbackIssues.length > 0) {
    overallConfidence = Math.min(overallConfidence, 0.5);
  }

  const confidenceNotJustified =
    overallConfidence >= 0.85 &&
    (rubricAlignment.unsupportedClaims.length > 0 ||
      !scoreAlignment.passed ||
      scoringIssues.length > 0 ||
      feedbackIssues.length > 0);

  checks.push(
    buildCheck(
      'confidence_not_justified',
      !confidenceNotJustified,
      confidenceNotJustified
        ? 'Confidence remained high despite unsupported or drifting reasoning.'
        : 'Confidence is consistent with the supporting evidence and checks.',
    ),
  );

  if (confidenceNotJustified) {
    overallConfidence = Math.min(overallConfidence, 0.6);
    reviewFlags.push({
      type: 'confidence_not_justified',
      severity: 'high',
      message: 'Confidence was reduced because the supporting checks did not justify a high-confidence decision.',
    });
  }

  transcription.uncertaintyFlags.forEach((flag) => {
    if (flag.type === 'uncertain_question_number') {
      reviewFlags.push({
        type: 'wrong_question_number',
        severity: 'medium',
        message: flag.message,
      });
      return;
    }

    if (flag.type === 'missing_answer') {
      reviewFlags.push({
        type: 'missing_answer',
        severity: 'medium',
        message: flag.message,
      });
      return;
    }

    reviewFlags.push({
      type: 'unclear_handwriting',
      severity: 'medium',
      message: flag.message,
    });
  });

  const confidenceLevel =
    overallConfidence >= 0.85 ? 'high' : overallConfidence >= 0.65 ? 'medium' : 'low';
  const failedChecks = checks.filter((check) => !check.passed);
  const reviewRequired =
    overallConfidence < REVIEW_CONFIDENCE_THRESHOLD ||
    reviewFlags.length > 0 ||
    failedChecks.length > 0;

  return {
    reliability: {
      confidenceLevel,
      confidenceScore: clampConfidence(overallConfidence),
      reviewRequired,
      reason:
        failedChecks[0]?.reason ||
        reviewFlags[0]?.message ||
        'The decision is internally consistent and evidence-backed.',
      checks,
    },
    reviewFlags: mergeReviewFlags(reviewFlags),
    overallConfidence: clampConfidence(overallConfidence),
  };
};

export const buildTeacherView = (
  transcription: TranscriptionResult,
  rubricAlignment: RubricAlignment,
  scoring: ScoreDecision,
  feedback: StudentFeedback,
  reliability: ReliabilityAssessment,
): TeacherViewSummary => {
  const evidenceById = new Map(
    transcription.evidenceSnippets.map((snippet) => [snippet.id, snippet.quote]),
  );
  const keyEvidence = feedback.evidenceIds
    .map((evidenceId) => evidenceById.get(evidenceId))
    .filter((quote): quote is string => Boolean(quote))
    .slice(0, 3);

  if (keyEvidence.length === 0 && transcription.rawAnswer.trim()) {
    keyEvidence.push(transcription.rawAnswer.trim());
  }

  const metCriteria = rubricAlignment.criteria
    .filter((criterion) => criterion.judgment === 'met')
    .map((criterion) => criterion.criterionText)
    .slice(0, 2);
  const missingCriteria = rubricAlignment.criteria
    .filter((criterion) => criterion.judgment === 'unmet')
    .map((criterion) => criterion.criterionText)
    .slice(0, 2);

  const explanationParts = [
    `Scored ${scoring.awardedMarks}/${scoring.maxMarks}.`,
    metCriteria.length > 0
      ? `Evidence met: ${metCriteria.join(' | ')}.`
      : 'No rubric criterion was fully met.',
    missingCriteria.length > 0
      ? `Still missing: ${missingCriteria.join(' | ')}.`
      : 'No major missing criterion was identified.',
  ];

  return {
    conciseExplanation: explanationParts.join(' '),
    keyEvidence,
    nextStep: feedback.tryThisNextTime,
    uncertaintyNote: reliability.reviewRequired ? reliability.reason : undefined,
  };
};

export const requiresTeacherReview = (questionEvaluations: QuestionEvaluation[]): boolean => {
  return questionEvaluations.some((questionEvaluation) => {
    if (questionEvaluation.reliability.reviewRequired) return true;
    if (questionEvaluation.overallConfidence < REVIEW_CONFIDENCE_THRESHOLD) return true;
    return questionEvaluation.reviewFlags.length > 0;
  });
};

export const computeReportConfidence = (questionEvaluations: QuestionEvaluation[]): number => {
  if (questionEvaluations.length === 0) return 0;
  const totalConfidence = questionEvaluations.reduce(
    (sum, questionEvaluation) => sum + clampConfidence(questionEvaluation.overallConfidence),
    0,
  );

  return clampConfidence(totalConfidence / questionEvaluations.length);
};
